import { MutableRefObject, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ColumnApi } from 'ag-grid-community';
import { isEqual } from 'lodash';
import { Store } from 'components/common/types/Store.types';
import { SearchConfig, SearchConfigColumn } from 'components/common/types/DirectSalesCampaign.types';
import { usePageFilters } from 'customHooks/usePageFilters';
import { deleteSearchConfig, getSearchConfig, saveSearchConfig } from 'modules/api/searchConfig';
import { isCanceledError } from 'modules/api/request';
import { addToastNotification, removeToastNotification } from 'store/notification/reducer';
import { changeMarketSearchConfig } from 'store/publisher/reducer';
import { cancelFunctions } from 'utils/cancelFunctions';
import { DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY } from '../Filters/consts';
import { DirectSalesDealManagementFilters } from '../Filters/Filters.types';
import { toColumnState, toFilters, toSearchConfigColumns, toSearchConfigFilters } from './transformSearchConfig';
import {
  EMPTY_SEARCH_CONFIG,
  LOAD_SEARCH_CONFIG_ERROR_TOAST_ID,
  SAVE_SEARCH_CONFIG_DELAY,
  SAVE_SEARCH_CONFIG_ERROR_TOAST_ID,
} from './consts';
import UseSearchTableConfig from './useSearchTableConfig.types';

export const useSearchTableConfig = (columnApiRef: MutableRefObject<ColumnApi | undefined>): UseSearchTableConfig => {
  const dispatch = useDispatch();
  const { marketId } = useSelector((state: Store) => state.publisher.configuration);
  const marketSearchConfig = useSelector((state: Store) => state.publisher.marketSearchConfig);
  const { filters, changeFilters, resetFilters } = usePageFilters<DirectSalesDealManagementFilters>(
    DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY,
  );
  const [isSearchConfigLoaded, setIsSearchConfigLoaded] = useState(false);

  const defaultColumnsRef = useRef<SearchConfigColumn[]>([]);
  const columnsRef = useRef<SearchConfigColumn[]>([]);
  const filtersRef = useRef(filters);
  const savedSearchConfigRef = useRef<SearchConfig>(EMPTY_SEARCH_CONFIG);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canSaveRef = useRef(false);
  const searchConfigMarketIdRef = useRef<string | null>(null);

  filtersRef.current = filters;

  const sendSearchConfig = (): void => {
    saveTimeoutRef.current = null;

    const searchConfigMarketId = searchConfigMarketIdRef.current;

    if (!canSaveRef.current || !searchConfigMarketId) return;

    const searchConfig: SearchConfig = {
      columns: columnsRef.current,
      filters: toSearchConfigFilters(filtersRef.current),
    };

    if (isEqual(searchConfig, savedSearchConfigRef.current)) return;

    const isDefaultSearchConfig = !searchConfig.columns.length && !searchConfig.filters.length;
    const request = isDefaultSearchConfig
      ? deleteSearchConfig(searchConfigMarketId, cancelFunctions)
      : saveSearchConfig(searchConfigMarketId, searchConfig, cancelFunctions);

    request
      .then(() => {
        savedSearchConfigRef.current = searchConfig;
        dispatch(changeMarketSearchConfig({ marketId: searchConfigMarketId, searchConfig }));
        dispatch(removeToastNotification(SAVE_SEARCH_CONFIG_ERROR_TOAST_ID));
      })
      .catch((error) => {
        if (isCanceledError(error)) return;

        dispatch(
          addToastNotification({
            id: SAVE_SEARCH_CONFIG_ERROR_TOAST_ID,
            header: 'Could not save your table settings',
            description: 'Your latest column and filter changes may not be kept.',
            type: 'Failure',
          }),
        );
      });
  };

  const persistSearchConfig = (): void => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(sendSearchConfig, SAVE_SEARCH_CONFIG_DELAY);
  };

  const flushSearchConfig = (): void => {
    if (!saveTimeoutRef.current) return;

    clearTimeout(saveTimeoutRef.current);
    sendSearchConfig();
  };

  const getColumns = (): SearchConfigColumn[] => toSearchConfigColumns(columnApiRef.current?.getColumnState() ?? []);

  const applyColumns = (columns: SearchConfigColumn[]): void => {
    if (!columns.length) return;

    columnApiRef.current?.applyColumnState({
      state: toColumnState(columns),
      applyOrder: true,
      defaultState: { sort: null },
    });
  };

  const fetchSearchConfig = async (): Promise<SearchConfig> => {
    const response = await getSearchConfig(marketId, cancelFunctions);
    const searchConfig: SearchConfig = { columns: response?.columns ?? [], filters: response?.filters ?? [] };

    dispatch(changeMarketSearchConfig({ marketId, searchConfig }));

    return searchConfig;
  };

  const loadSearchConfig = async (): Promise<void> => {
    searchConfigMarketIdRef.current = marketId;

    try {
      const searchConfigForMarket = marketSearchConfig?.marketId === marketId ? marketSearchConfig.searchConfig : null;
      const { columns, filters: searchConfigFilters } = searchConfigForMarket ?? (await fetchSearchConfig());

      columnsRef.current = columns;
      savedSearchConfigRef.current = { columns, filters: searchConfigFilters };
      canSaveRef.current = true;

      applyColumns(columns);
      resetFilters(DIRECT_SALES_CAMPAIGN_PAGE_FILTERS_KEY);
      changeFilters(toFilters(searchConfigFilters));
    } catch (error) {
      if (isCanceledError(error)) return;

      dispatch(
        addToastNotification({
          id: LOAD_SEARCH_CONFIG_ERROR_TOAST_ID,
          header: 'Could not load your table settings',
          description: 'Changes to columns and filters will not be saved.',
          type: 'Failure',
        }),
      );
    }

    setIsSearchConfigLoaded(true);
  };

  const initialiseSearchConfigColumns = (): void => {
    defaultColumnsRef.current = getColumns();

    if (isSearchConfigLoaded) {
      applyColumns(columnsRef.current);

      return;
    }

    loadSearchConfig();
  };

  const saveSearchConfigColumns = (): void => {
    const columns = getColumns();

    columnsRef.current = isEqual(columns, defaultColumnsRef.current) ? [] : columns;
    persistSearchConfig();
  };

  const resetSearchConfigColumns = (): void => {
    applyColumns(defaultColumnsRef.current);
    saveSearchConfigColumns();
  };

  useEffect(() => {
    if (isSearchConfigLoaded) persistSearchConfig();
  }, [isSearchConfigLoaded, filters]);

  useEffect(() => {
    if (!searchConfigMarketIdRef.current || searchConfigMarketIdRef.current === marketId) return;

    flushSearchConfig();
    canSaveRef.current = false;
    applyColumns(defaultColumnsRef.current);
    loadSearchConfig();
  }, [marketId]);

  useEffect(() => flushSearchConfig, []);

  return {
    filters,
    isSearchConfigLoaded,
    initialiseSearchConfigColumns,
    saveSearchConfigColumns,
    resetSearchConfigColumns,
  };
};
